import { ArrowUturnLeft, History } from "@medusajs/icons"
import { Button, Drawer, IconButton, Input, Select, Tabs, Text, toast } from "@medusajs/ui"
import { useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { DataTable, EmptyState, ErrorState, PageHeader, SearchInput, Section, Thumb, usePage } from "@/components/ui"
import { useActivity } from "@/lib/api"
import { useStoreContext } from "@/lib/catalog"
import { formatDate } from "@/lib/format"
import { ADJUST_REASONS, reasonLabel, useAdjustInventory, useInventory, type AdjustReason, type InventoryRow } from "@/lib/inventory"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"

const PAGE = 50
const LOW = 10
const FILTERS = [
  { value: "", label: "All" },
  { value: "low", label: "Low stock" },
  { value: "out", label: "Out of stock" },
]

/**
 * Shopify's Inventory page: every tracked SKU at one location, edit On hand
 * inline (or +/- adjust), save all changes with a reason, see each item's history.
 */
export function InventoryPage() {
  const [params, setParams] = useSearchParams()
  const { pageIndex, q, setPage, setQ } = usePage(params, setParams)
  const ctx = useStoreContext()
  const [locationId, setLocationId] = useState<string | undefined>()
  const loc = locationId ?? ctx.data?.locationId
  const filter = params.get("filter") ?? ""
  const { data, isLoading, error } = useInventory({ q, locationId: loc, limit: PAGE, offset: pageIndex * PAGE })
  const canWrite = useCan()("inventory:write")
  const adjust = useAdjustInventory()
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [reason, setReason] = useState<AdjustReason>("correction")
  const [history, setHistory] = useState<InventoryRow | null>(null)

  const rows = (data?.rows ?? []).filter((r) => (filter === "low" ? r.available > 0 && r.available <= LOW : filter === "out" ? r.available <= 0 : true))
  const changed = Object.entries(edits).filter(([id, v]) => {
    const r = data?.rows.find((x) => x.id === id)
    return r && v.trim() !== "" && /^-?\d+$/.test(v.trim()) && Number(v) !== r.on_hand
  })
  const invalid = Object.values(edits).some((v) => v.trim() !== "" && !/^-?\d+$/.test(v.trim()))

  const setFilter = (v: string) => {
    const next = new URLSearchParams(params)
    v ? next.set("filter", v) : next.delete("filter")
    setParams(next)
  }

  const saveAll = () => {
    if (!loc || !changed.length) return
    adjust.mutate(
      { location_id: loc, reason, changes: changed.map(([id, v]) => ({ inventory_item_id: id, stocked_quantity: Number(v) })) },
      {
        onSuccess: (r) => {
          toast.success(`Updated ${r.adjusted} item${r.adjusted === 1 ? "" : "s"}`)
          setEdits({})
        },
        onError: (err) => toast.error(errorMessage(err)),
      }
    )
  }

  return (
    <div className="pb-24">
      <PageHeader
        title="Inventory"
        actions={
          (ctx.data?.locations.length ?? 0) > 1 && (
            <div className="w-56">
              <Select value={loc} onValueChange={(v) => { setLocationId(v); setEdits({}) }}>
                <Select.Trigger>
                  <Select.Value placeholder="Location" />
                </Select.Trigger>
                <Select.Content>
                  {ctx.data!.locations.map((l) => (
                    <Select.Item key={l.id} value={l.id}>{l.name}</Select.Item>
                  ))}
                </Select.Content>
              </Select>
            </div>
          )
        }
      />
      <Section>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <Tabs value={filter} onValueChange={setFilter}>
            <Tabs.List>
              {FILTERS.map((f) => (
                <Tabs.Trigger key={f.value} value={f.value}>{f.label}</Tabs.Trigger>
              ))}
            </Tabs.List>
          </Tabs>
          <SearchInput value={q} onChange={setQ} placeholder="Search by SKU or product" />
        </div>
        {error ? (
          <ErrorState error={error} />
        ) : !ctx.isLoading && !loc ? (
          <EmptyState title="No stock location" body="Run the store setup to create your ship-from location." />
        ) : (
          <DataTable
            rowKey={(r) => r.id}
            rows={rows}
            count={data?.count ?? 0}
            pageSize={PAGE}
            pageIndex={pageIndex}
            onPageChange={setPage}
            loading={isLoading}
            mobile={(r) => (
              <div className="flex items-start gap-x-3">
                <Thumb src={r.thumbnail} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium">{r.product_title ?? r.title ?? "Untitled item"}</div>
                  <div className="text-[12px] text-ui-fg-subtle">
                    {[r.variant_title !== "Default Title" ? r.variant_title : null, r.sku].filter(Boolean).join(" · ")}
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-x-3">
                    <div className="text-[12px] text-ui-fg-subtle">
                      Available <span className={r.available <= 0 ? "font-semibold text-ui-fg-error" : "font-semibold text-ui-fg-base"}>{r.available}</span> · Committed {r.committed}
                    </div>
                    {canWrite ? (
                      <div className="flex items-center gap-x-1" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[12px] text-ui-fg-subtle">On hand</span>
                        <OnHandInput
                          value={edits[r.id] ?? String(r.on_hand)}
                          changed={edits[r.id] !== undefined && edits[r.id] !== String(r.on_hand)}
                          onChange={(v) => setEdits((e) => ({ ...e, [r.id]: v }))}
                          onReset={() => setEdits(({ [r.id]: _, ...rest }) => rest)}
                        />
                      </div>
                    ) : (
                      <span className="text-[12px]">On hand {r.on_hand}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
            empty={<EmptyState title={filter ? "Nothing matches this filter" : "No tracked inventory yet"} body="Products with “Track quantity” on appear here." />}
            columns={[
              {
                key: "product",
                header: "Product",
                cell: (r) => (
                  <div className="flex items-center gap-x-3">
                    <Thumb src={r.thumbnail} size={36} />
                    <div className="min-w-0">
                      {r.product_id ? (
                        <Link to={`/products/${r.product_id}`} className="txt-compact-small-plus hover:underline">{r.product_title}</Link>
                      ) : (
                        <Text size="small" weight="plus">{r.product_title ?? r.title ?? "Untitled item"}</Text>
                      )}
                      {r.variant_title && r.variant_title !== "Default Title" && <Text size="xsmall" className="text-ui-fg-muted">{r.variant_title}</Text>}
                    </div>
                  </div>
                ),
              },
              { key: "sku", header: "SKU", cell: (r) => <span className="text-ui-fg-subtle">{r.sku ?? "—"}</span> },
              { key: "committed", header: "Committed", cell: (r) => r.committed },
              {
                key: "available",
                header: "Available",
                cell: (r) => {
                  const next = edits[r.id] !== undefined && /^-?\d+$/.test(edits[r.id]) ? Number(edits[r.id]) - r.committed : r.available
                  return <span className={next <= 0 ? "text-ui-fg-error" : next <= LOW ? "text-ui-tag-orange-text" : ""}>{next}</span>
                },
              },
              {
                key: "on_hand",
                header: "On hand",
                cell: (r) =>
                  canWrite ? (
                    <OnHandInput
                      value={edits[r.id] ?? String(r.on_hand)}
                      changed={edits[r.id] !== undefined && edits[r.id] !== String(r.on_hand)}
                      onChange={(v) => setEdits((e) => ({ ...e, [r.id]: v }))}
                      onReset={() => setEdits(({ [r.id]: _, ...rest }) => rest)}
                    />
                  ) : (
                    r.on_hand
                  ),
              },
              {
                key: "history",
                header: "",
                className: "w-10",
                cell: (r) => (
                  <IconButton size="small" variant="transparent" aria-label="Inventory history" onClick={() => setHistory(r)}>
                    <History />
                  </IconButton>
                ),
              },
            ]}
          />
        )}
      </Section>

      {canWrite && (changed.length > 0 || invalid) && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-ui-border-base bg-ui-bg-base shadow-elevation-flyout lg:bottom-0 lg:left-[var(--tf-content-left,15rem)] lg:right-0">
          <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-3 px-4 py-3 lg:px-8">
            <Text size="small" weight="plus" className={invalid ? "text-ui-fg-error" : "text-ui-fg-subtle"}>
              {invalid ? "Quantities must be whole numbers" : `${changed.length} unsaved change${changed.length === 1 ? "" : "s"}`}
            </Text>
            <div className="flex items-center gap-x-2">
              <div className="w-52">
                <Select value={reason} onValueChange={(v) => setReason(v as AdjustReason)}>
                  <Select.Trigger>
                    <Select.Value />
                  </Select.Trigger>
                  <Select.Content>
                    {ADJUST_REASONS.map((r) => (
                      <Select.Item key={r.value} value={r.value}>{r.label}</Select.Item>
                    ))}
                  </Select.Content>
                </Select>
              </div>
              <Button size="small" variant="secondary" onClick={() => setEdits({})}>Discard</Button>
              <Button size="small" isLoading={adjust.isPending} disabled={invalid || !changed.length} onClick={saveAll}>Save</Button>
            </div>
          </div>
        </div>
      )}

      {history && <HistoryDrawer row={history} locationName={ctx.data?.locations.find((l) => l.id === loc)?.name} onClose={() => setHistory(null)} />}
    </div>
  )
}

/** Type a new on-hand number, or "+5" / "-2" to adjust by that much. */
function OnHandInput({ value, changed, onChange, onReset }: { value: string; changed: boolean; onChange: (v: string) => void; onReset: () => void }) {
  const [draft, setDraft] = useState<string | null>(null)
  return (
    <div className="flex items-center gap-x-1">
      <Input
        size="small"
        className={"w-20 " + (changed ? "border-ui-border-interactive" : "")}
        inputMode="numeric"
        value={draft ?? value}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft == null) return
          const t = draft.trim()
          const m = /^([+-])\s*(\d+)$/.exec(t)
          if (m && (t.startsWith("+") || draft !== value)) {
            const base = Number(value) || 0
            onChange(String(m[1] === "+" ? base + Number(m[2]) : base - Number(m[2])))
          } else onChange(t)
          setDraft(null)
        }}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      />
      {changed && (
        <IconButton size="2xsmall" variant="transparent" aria-label="Undo" onClick={onReset}>
          <ArrowUturnLeft />
        </IconButton>
      )}
    </div>
  )
}

function HistoryDrawer({ row, locationName, onClose }: { row: InventoryRow; locationName?: string; onClose: () => void }) {
  const { data, isLoading } = useActivity({ resource_id: row.id, limit: 100, offset: 0 })
  const entries = (data?.activity ?? []).filter((a: any) => a.action === "inventory.adjusted")
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>History · {row.sku ?? row.product_title}</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="overflow-y-auto">
          <Text size="small" className="mb-3 text-ui-fg-subtle">
            {row.product_title}
            {row.variant_title && row.variant_title !== "Default Title" ? ` · ${row.variant_title}` : ""} — {row.on_hand} on hand{locationName ? ` at ${locationName}` : ""}
          </Text>
          {isLoading ? (
            <Text size="small" className="text-ui-fg-muted">Loading…</Text>
          ) : entries.length ? (
            <ul className="divide-y rounded-md border">
              {entries.map((a: any) => {
                const m = a.metadata ?? {}
                return (
                  <li key={a.id} className="flex items-start justify-between gap-x-3 px-3 py-2">
                    <div>
                      <Text size="small" weight="plus">{reasonLabel(m.reason ?? "correction")}</Text>
                      <Text size="xsmall" className="text-ui-fg-muted">
                        {formatDate(a.created_at)} · {a.actor_email ?? "System"}
                      </Text>
                      {m.note && <Text size="xsmall" className="text-ui-fg-subtle">{m.note}</Text>}
                    </div>
                    <div className="text-right">
                      <Text size="small" weight="plus" className={m.delta > 0 ? "text-ui-fg-interactive" : m.delta < 0 ? "text-ui-fg-error" : ""}>
                        {m.delta > 0 ? "+" : ""}
                        {m.delta}
                      </Text>
                      <Text size="xsmall" className="text-ui-fg-muted">{m.from} → {m.to}</Text>
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : (
            <EmptyState title="No adjustments yet" body="Changes saved from Inventory or the product page show up here." />
          )}
        </Drawer.Body>
      </Drawer.Content>
    </Drawer>
  )
}
